import React from 'react';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from './ui/carousel';
import { cn } from './ui/utils';

export interface Carousel002Image {
  src: string;
  alt: string;
}

interface Carousel002Props {
  images: Carousel002Image[];
  className?: string;
  showPagination?: boolean;
  showNavigation?: boolean;
  loop?: boolean;
  onIndexChange?: (index: number) => void;
}

const Carousel_002: React.FC<Carousel002Props> = ({
  images,
  className,
  showPagination = true,
  showNavigation = true,
  loop = true,
  onIndexChange,
}) => {
  const [api, setApi] = React.useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    const syncIndex = () => {
      const nextIndex = api.selectedScrollSnap();
      setActiveIndex(nextIndex);
      onIndexChange?.(nextIndex);
    };

    syncIndex();
    api.on('select', syncIndex);
    api.on('reInit', syncIndex);

    return () => {
      api.off('select', syncIndex);
      api.off('reInit', syncIndex);
    };
  }, [api, onIndexChange]);

  if (images.length === 0) {
    return null;
  }

  const shouldShowControls = images.length > 1;

  return (
    <div className={cn('relative w-full', className)}>
      <Carousel
        setApi={setApi}
        opts={{
          align: 'center',
          loop: loop && shouldShowControls,
          skipSnaps: false,
          dragFree: false,
        }}
        className="w-full"
      >
        <CarouselContent className="ml-0">
          {images.map((image, index) => (
            <CarouselItem key={`${image.src}-${index}`} className="pl-0">
              <a
                href={image.src}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open image ${index + 1} in new tab`}
                className="block w-full"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  className="block w-full h-auto max-h-[520px] object-contain bg-white/60 dark:bg-gray-950/40"
                />
              </a>
            </CarouselItem>
          ))}
        </CarouselContent>
        {showNavigation && shouldShowControls && (
          <>
            <CarouselPrevious className="left-2 top-1/2 -translate-y-1/2 bg-black/55 text-white hover:bg-black/65 border-black/25 dark:bg-black/55 dark:text-white dark:border-white/15" />
            <CarouselNext className="right-2 top-1/2 -translate-y-1/2 bg-black/55 text-white hover:bg-black/65 border-black/25 dark:bg-black/55 dark:text-white dark:border-white/15" />
          </>
        )}
      </Carousel>

      {showPagination && shouldShowControls && (
        <div className="pointer-events-none absolute bottom-2 left-1/2 z-10 -translate-x-1/2">
          <div className="rounded-full bg-black/35 px-2 py-1 backdrop-blur-sm">
            <div className="flex items-center gap-1">
              {images.map((_, index) => (
                <span
                  key={`dot-${index}`}
                  className={cn(
                    'h-1.5 w-1.5 rounded-full transition-all',
                    index === activeIndex ? 'bg-white' : 'bg-white/45',
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { Carousel_002 };

